// The app's store: one household ledger from the service, shared by both presentations, so the Chat/Screens switch
// carries everything. The phone never sets a commercial state itself: every purchase, appointment, choice and request is
// a ledger action on the Mac (shared/src/ledger.ts). Only where the household is looking is kept in the browser session.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Records, PhotoRecord } from '@roundstead/shared/library';
import type { Action, Household, Payment } from '@roundstead/shared/ledger';
import type { Screen } from '@roundstead/shared/replies';
import { DEFAULT_SCENARIO, type Scenario } from '@roundstead/shared/scenario';
import { api, preparePhoto, Refused, Unavailable } from './api.ts';

export type Mode = 'chat' | 'ui';
export type View = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
/** A section of the club home: a purchased visit, a new visit request, deals, records, renewal/leaving, or a case. */
export type Page = null | `visit:${string}` | 'request' | 'deals' | 'records' | 'membership' | `case:${string}`;
export type Entry =
  | { id: number; from: 'home'; text: string }
  | { id: number; from: 'ai'; reply: string; fallback?: boolean }
  | { id: number; from: 'local'; kind: 'yes' };
export type Sheet = null | 'ask' | 'membership' | 'visits' | 'unresolved' | 'terms' | 'route';
/** Exception states (content.json exceptions). Person states follow the case owner the facilitator records. */
export type Exc = 'photo-skipped' | 'not-served' | 'intake-restricted' | 'decline' | 'resume' | 'terms-changed' | 'pay-processing' | 'pay-failed' | 'pay-unknown'
  | 'activation-pending' | 'activation-problem' | 'person-requested' | 'person-accepted' | 'person-unavailable' | 'urgent' | 'unavailable' | 'bad-upload';
export interface LocalPhoto extends PhotoRecord { preview?: string; file?: File; serverId?: string }

interface Saved { mode: Mode; view: View; page: Page; entries: Entry[]; seenVersion?: string | null; ackStopped?: string[] }
const params = new URLSearchParams(location.search);
const code = params.get('s') ?? '';
const urlMode: Mode | null = params.get('c') === 'chat' ? 'chat' : params.get('c') === 'ui' ? 'ui' : null;
// &wait=held on the link holds preparation for the prototype control; the facilitator can also set it from the panel.
const urlHeld = params.get('wait') === 'held';
const KEY = `rs:${code}`;
const client = api(code);

function load(): Saved | null {
  try { return JSON.parse(sessionStorage.getItem(KEY) ?? 'null'); } catch { return null; /* private mode: start fresh */ }
}

/** The screen name the assistant gets, so its reply fits beside what the household sees. */
export function screenOf(view: View, page: Page): Screen {
  if (page) return page.startsWith('visit:') ? 'visit' : page.startsWith('case:') ? 'care' : page === 'membership' ? 'renewal' : page as Screen;
  return (['offer', 'context', 'preparing', 'quote', 'review', 'payment', 'membership', 'care'] as const)[view - 1];
}

export function useStoreValue() {
  const prior = useMemo(load, []);
  const saved: Saved = prior ?? { mode: urlMode ?? 'ui', view: 1, page: null, entries: [] };
  const [mode, setModeRaw] = useState<Mode>(saved.mode);
  const [view, setViewRaw] = useState<View>(saved.view);
  const [page, setPageRaw] = useState<Page>(saved.page ?? null);
  const [entries, setEntries] = useState<Entry[]>(saved.entries);
  const [seenVersion, setSeenVersion] = useState<string | null>(saved.seenVersion ?? null);
  const [ackStopped, setAckStopped] = useState<string[]>(saved.ackStopped ?? []);
  const [records, setRecords] = useState<Records | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [local, setLocal] = useState<LocalPhoto[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sheet, setSheetRaw] = useState<Sheet>(null);
  const [route, setRoute] = useState<{ title: string; value: string } | null>(null);
  const [exc, setExc] = useState<Exc | null>(null);
  const [down, setDown] = useState(false);
  const [announce, setAnnounce] = useState('');
  const [acceptPing, setAcceptPing] = useState(0);
  const nextId = useRef(Math.max(0, ...saved.entries.map((e) => e.id)) + 1);
  const scenario: Scenario = records?.scenario ?? DEFAULT_SCENARIO;
  const heldWait = urlHeld || scenario.wait === 'held';
  const h: Household | null = records?.household ?? null;

  // The event log: what was tapped and shown, never what was typed. Sent in small batches to the session on the Mac.
  const queue = useRef<unknown[]>([]);
  const log = useCallback((type: string, detail?: Record<string, unknown>) => { queue.current.push({ t: Date.now(), type, ...detail }); }, []);
  useEffect(() => {
    if (!code) return;
    const flush = () => { if (!queue.current.length) return; const batch = queue.current.splice(0); client.events(batch).catch(() => queue.current.unshift(...batch)); };
    const t = setInterval(flush, 2000);
    const hide = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', hide); addEventListener('pagehide', flush);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', hide); removeEventListener('pagehide', flush); };
  }, []);
  useEffect(() => {
    const tap = (e: MouseEvent) => {
      const el = (e.target as HTMLElement).closest('button, [role=tab], label.rs-option, a');
      if (el && !el.closest('.rs-facilitator')) log('tap', { label: (el.getAttribute('aria-label') ?? (el as HTMLElement).innerText.split('\n')[0] ?? '').trim().slice(0, 80) });
    };
    document.addEventListener('click', tap, true);
    return () => document.removeEventListener('click', tap, true);
  }, [log]);

  useEffect(() => {
    try { sessionStorage.setItem(KEY, JSON.stringify({ mode, view, page, entries, seenVersion, ackStopped })); } catch { /* not essential */ }
  }, [mode, view, page, entries, seenVersion, ackStopped]);
  // Where the household is also goes to the Mac, so a return on another device resumes there (after the resume check).
  useEffect(() => { if (loaded && exc !== 'resume' && !down) client.progress(view, page).catch(() => {}); }, [loaded, view, page]);

  const fail = (e: unknown) => { if (e instanceof Unavailable) { setDown(true); log('unavailable', { reason: e.message }); } else setError((e as Error).message); };
  const refresh = useCallback(() => client.records().then((r) => { setRecords(r); setDown(false); return r; }), []);
  useEffect(() => {
    if (!code) return;
    refresh().then((r) => {
      if (!prior && !urlMode && r.scenario) setModeRaw(r.scenario.start);
      if (!prior && r.started) { setExc('resume'); log('exception', { key: 'resume' }); }
      log('open', { mode: prior?.mode ?? urlMode ?? r.scenario?.start, resumed: !!prior });
    }).catch(fail).finally(() => setLoaded(true));
  }, []);
  // Keep the records fresh so the facilitator's outcomes (payment, activation, technician results) reach the phone.
  useEffect(() => {
    if (!code) return;
    const t = setInterval(() => { if (document.visibilityState === 'visible') refresh().catch(fail); }, down ? 3000 : 4000);
    return () => clearInterval(t);
  }, [down]);

  const push = (e: { from: 'home'; text: string } | { from: 'ai'; reply: string; fallback?: boolean } | { from: 'local'; kind: 'yes' }) =>
    setEntries((xs) => [...xs, { ...e, id: nextId.current++ } as Entry]);
  const setMode = (m: Mode) => { setModeRaw(m); log('mode', { mode: m }); };
  const setView = (v: View) => { setViewRaw(v); setPageRaw(null); setSheetRaw(null); setExc(null); window.scrollTo({ top: 0 }); log('screen', { view: v }); };
  const openPage = (p: Page) => { setPageRaw(p); setSheetRaw(null); setExc(null); window.scrollTo({ top: 0 }); log('page', { page: p }); };
  const setSheet = (s: Sheet) => { setSheetRaw(s); if (s) log('sheet', { sheet: s }); };
  const openExc = (k: Exc) => { setExc(k); setSheetRaw(null); window.scrollTo({ top: 0 }); log('exception', { key: k }); };
  const closeExc = () => { setExc(null); window.scrollTo({ top: 0 }); };
  const openRoute = (title: string, value: string) => { setRoute({ title, value }); setSheet('route'); };
  /** Brings the exact purchase control into view (the chat-led record panel opens for it). */
  const openAccept = () => setAcceptPing(Date.now());

  /** One ledger action. A refused action changes nothing; its reason opens the matching state or shows plainly. */
  const act = useCallback(async (a: Action): Promise<Records | null> => {
    setError(null);
    try { const r = await client.act(a); setRecords(r); return r; }
    catch (e) {
      if (e instanceof Refused) {
        log('refused', { action: a.type, code: e.code });
        if (e.code === 'not_served') openExc('not-served');
        else if (e.code === 'intake_restricted') openExc('intake-restricted');
        else if (e.code === 'stale_version') openExc('terms-changed');
        else if (e.code === 'attempt_open') { const r = await refresh().catch(() => null); const open = r?.household.payments.find((p) => p.state === 'processing' || p.state === 'unknown'); openExc(open ? `pay-${open.state}` as Exc : 'pay-unknown'); return null; }
        else setError(e.message);
        refresh().catch(() => {});
        return null;
      }
      fail(e); return null;
    }
  }, []);

  const screen = screenOf(view, page);
  const turn = useCallback(async (msg: { text?: string; photoIds?: string[] }, shown?: string) => {
    if (shown) push({ from: 'home', text: shown });
    // "Yes" alone on a review never accepts: the screen asks which, and points to the exact purchase control.
    if ((view === 5 || page === 'request') && msg.text && /^\s*(yes|yeah|yep|yup|ok|okay|sure|y|do it|go ahead|that one)[\s.!]*$/i.test(msg.text)) { push({ from: 'local', kind: 'yes' }); log('yes_ambiguous'); return null; }
    setBusy(msg.photoIds?.length ? 'Looking at your photos' : 'Working on a reply'); setError(null);
    try {
      const out = await client.turn({ ...msg, screen });
      setRecords(out.records);
      push({ from: 'ai', reply: out.reply, fallback: out.fallback });
      setAnnounce('The assistant replied.');
      if (out.next === 'urgent') openExc('urgent');
      return out;
    } catch (e) { fail(e); return null; } finally { setBusy(null); }
  }, [view, page, screen]);

  const uploadOne = async (p: LocalPhoto): Promise<string | null> => {
    setLocal((xs) => xs.map((x) => (x.id === p.id ? { ...x, state: 'uploading' } : x)));
    try {
      const rec = await client.photo(p.file!.name, await preparePhoto(p.file!));
      setLocal((xs) => xs.map((x) => (x.id === p.id ? { ...x, state: 'received', serverId: rec.id } : x)));
      return rec.id;
    } catch (e) {
      setLocal((xs) => xs.map((x) => (x.id === p.id ? { ...x, state: 'failed' } : x)));
      fail(e); return null;
    }
  };
  const addPhotos = useCallback(async (files: FileList | File[]) => {
    const list = Array.from(files); if (!list.length) return;
    const start = local.length;
    const items: LocalPhoto[] = list.map((f, i) => ({ id: `l${Date.now()}-${i}`, name: `Photo ${start + i + 1}`, state: 'uploading', file: f, preview: URL.createObjectURL(f) }));
    setLocal((xs) => [...xs, ...items]);
    log('photos', { count: list.length });
    const ids = (await Promise.all(items.map(uploadOne))).filter(Boolean) as string[];
    if (ids.length) await turn({ photoIds: ids }, `${ids.length} ${ids.length === 1 ? 'photo' : 'photos'} added`);
  }, [local.length, turn]);
  const retry = useCallback(async (id: string) => {
    const p = local.find((x) => x.id === id); if (!p) return;
    const sid = await uploadOne(p); if (sid) await turn({ photoIds: [sid] }, '1 photo added');
  }, [local, turn]);
  const removePhoto = async (id: string) => {
    const lp = local.find((x) => x.id === id || x.serverId === id);
    setLocal((xs) => xs.filter((x) => x.id !== id && x.serverId !== id));
    const sid = lp ? lp.serverId : id;
    if (sid) { try { setRecords(await client.removePhoto(sid)); } catch (e) { fail(e); } }
  };

  /* Joining (F1) and its forks, each landing on the state the facilitator set. */
  const begin = async () => { await act({ type: 'start' }); setView(2); };
  const prepare = async () => { const r = await act({ type: 'prepare' }); if (r) setView(3); };
  const issue = async () => { const r = await act({ type: 'issue' }); if (r) { setView(4); setAnnounce('Your quote is ready.'); } };
  /** Simulated acceptance and payment of exactly the reviewed purchase. The outcome follows the attempt's actual state. */
  const pay = async () => {
    const p = records?.purchase; if (!p || p.total === null) return;
    const kind = p.quote.kind;
    const r = await act({ type: 'accept', ref: p.quote.ref, version: p.quote.version, total: p.total });
    if (!r) return;
    const attempt = r.household.payments.at(-1)!;
    log('payment', { outcome: attempt.state, attempt: attempt.id, total: p.total });
    followAttempt(attempt, kind, r.household);
  };
  const followAttempt = (a: Payment, kind: 'join' | 'visit' | 'renewal', hh: Household) => {
    if (a.state === 'confirmed') {
      if (kind === 'join') setView(6);
      else if (kind === 'visit') openPage(`visit:${hh.orders.at(-1)!.id}`);
      else { setExc(null); openPage('membership'); }
      setAnnounce(`Payment confirmed, receipt ${a.receipt}.`);
    } else openExc(`pay-${a.state}` as Exc);
  };
  const seeStatus = () => {
    const m = h?.membership.state;
    if (m === 'activation_pending') openExc('activation-pending'); else if (m === 'activation_problem') openExc('activation-problem'); else setView(7);
  };
  const contact = async (about?: string | null) => {
    const r = await act({ type: 'contact', about: about ?? null });
    const k = r?.household.cases.find((c) => c.kind === 'person' && c.state !== 'closed');
    if (k) openExc(`person-${k.owner}` as Exc);
  };
  const notNow = () => { if (h && h.membership.state !== 'none') setView(8); else openExc('decline'); };

  // A payment attempt still processing or unknown settles to the facilitator's outcome; the screen follows it.
  useEffect(() => {
    if (!h || !exc?.startsWith('pay-')) return;
    const a = h.payments.at(-1);
    if (a && `pay-${a.state}` !== exc) followAttempt(a, a.for, h);
  }, [h?.payments.map((p) => p.state).join()]);
  // Activation pending or a problem follows the facilitator's outcome too.
  useEffect(() => {
    if (!h) return;
    if ((exc === 'activation-pending' || exc === 'activation-problem') && h.membership.state === 'active') setView(7);
    else if (exc === 'activation-pending' && h.membership.state === 'activation_problem') openExc('activation-problem');
  }, [h?.membership.state]);
  // A person case shows the owner state the facilitator records.
  useEffect(() => {
    const k = h?.cases.find((c) => c.kind === 'person' && c.state !== 'closed');
    if (k && exc?.startsWith('person-') && exc !== `person-${k.owner}`) openExc(`person-${k.owner}` as Exc);
  }, [h?.cases.map((c) => c.owner).join()]);
  // A photo the service stopped processing on (instructions inside it) opens "Upload stopped" once.
  useEffect(() => {
    const fresh = (records?.stopped ?? []).filter((id) => !ackStopped.includes(id));
    if (fresh.length) { setAckStopped((a) => [...a, ...fresh]); openExc('bad-upload'); }
  }, [records?.stopped?.join()]);
  // An issued quote that changes while the household reviews it: "Terms changed", reviewed again before acceptance.
  const q = records?.purchase?.quote ?? null;
  useEffect(() => {
    if (!q) return;
    const key = `${q.ref} ${q.version}`;
    if ((view === 4 || view === 5 || page === 'request') && !seenVersion) setSeenVersion(key);
    else if (seenVersion && seenVersion.split(' ')[0] === q.ref && key !== seenVersion && (view === 5 || page === 'request')) openExc('terms-changed');
    else if (seenVersion && seenVersion.split(' ')[0] !== q.ref) setSeenVersion(key);
  }, [q?.ref, q?.version, view, page]);
  const acceptVersion = () => { if (q) setSeenVersion(`${q.ref} ${q.version}`); };

  const shownIds = new Set(local.map((p) => p.serverId).filter(Boolean));
  const photos: LocalPhoto[] = [...(records?.photos ?? []).filter((p) => !shownIds.has(p.id)), ...local];
  const serverId = (p: LocalPhoto) => p.serverId ?? (p.id.startsWith('l') ? undefined : p.id);

  return { code, mode, setMode, view, setView, page, openPage, entries, records, h, loaded, photos, serverId, busy, error, setError, sheet, setSheet,
           announce, setAnnounce, turn, addPhotos, retry, removePhoto, scenario, heldWait, exc, openExc, closeExc, down, acceptPing, openAccept,
           refresh, route, openRoute, notNow, contact, act, begin, prepare, issue, pay, seeStatus, acceptVersion, screen, log };
}

export type Store = ReturnType<typeof useStoreValue>;
const Ctx = createContext<Store | null>(null);
export function StoreProvider({ children }: { children: ReactNode }) { return <Ctx.Provider value={useStoreValue()}>{children}</Ctx.Provider>; }
export function useStore() { const s = useContext(Ctx); if (!s) throw new Error('no store'); return s; }
